import React, { useMemo, useState } from "react";
import { CalendarPlus, ChevronLeft, ChevronRight, Eraser, FileUp, RefreshCw, TriangleAlert, WifiOff } from "lucide-react";
import { Pharmacy, Shift } from "../types";
import { Alert, Badge, Button, Card, Empty, ICON, IconButton, Modal, Muted, Skeleton, cx, useToast } from "./ui";
import { DayDrawer } from "./DayDrawer";
import { OriginalExcelUploader } from "../components/OriginalExcelUploader";
import {
  DIAS3,
  DIAS_GRILLA,
  MESES,
  capitalizar,
  dos,
  hoy,
  listarDias,
  nombreCorto,
  turnoActivo,
} from "./turnos";

interface TurnosPageProps {
  pharmacies: Pharmacy[];
  shifts: Shift[];
  /** Mes que se está mirando: año y mes 1-12. */
  year: number;
  month: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  /** Primera carga sin datos todavía. */
  loading: boolean;
  /** La última carga de farmacias o turnos falló. */
  loadFailed: boolean;
  onRetry: () => void;
  /** «Actualizar»: regenera la caché de la web y vuelve a traer los datos. Lanza un Error si falla. */
  onRefresh: () => Promise<void>;
  selectedDate: string | null;
  onSelectDate: (date: string | null) => void;
  /** Lanzan un Error con el motivo si el servidor no guardó (P-13). */
  onAddPharmacy: (date: string, pharmacyId: string) => Promise<void>;
  onRemovePharmacy: (date: string, pharmacyId: string) => Promise<void>;
  onClearMonth: (year: number, month: number) => Promise<void>;
  uploadOpen: boolean;
  onUploadOpenChange: (open: boolean) => void;
  /** Se llama cuando el Excel se cargó bien, para volver a traer los datos. */
  onUploaded: () => void;
}

function textoFaltantes(dias: number[]): string {
  if (dias.length === 0) return "El mes está completo.";
  if (dias.length === 1) return `El ${dias[0]}. Tocalo para completarlo.`;
  if (dias.length <= 4) return `${capitalizar(listarDias(dias))}. Tocalos para completarlos.`;
  return "Están en ámbar en el calendario. Tocalos para completarlos.";
}

const LBL = "text-xs font-medium text-adm-t55";
const STAT = "flex min-w-0 flex-col gap-2 px-5 py-[18px] max-md:px-4 max-md:py-3.5";
const STAT_V = "text-[30px] font-medium leading-none tracking-[-0.03em] text-adm-text max-md:text-2xl";

export function TurnosPage({
  pharmacies,
  shifts,
  year,
  month,
  onPrevMonth,
  onNextMonth,
  loading,
  loadFailed,
  onRetry,
  onRefresh,
  selectedDate,
  onSelectDate,
  onAddPharmacy,
  onRemovePharmacy,
  onClearMonth,
  uploadOpen,
  onUploadOpenChange,
  onUploaded,
}: TurnosPageProps) {
  const toast = useToast();
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const byId = useMemo(() => new Map(pharmacies.map((p) => [p.id, p])), [pharmacies]);
  const shiftByDate = useMemo(() => new Map(shifts.map((s) => [s.date, s])), [shifts]);
  // Solo cuentan las farmacias del catálogo: es lo que ven la web y el bot
  const enCatalogo = (date: string) =>
    (shiftByDate.get(date)?.pharmacyIds || [])
      .map((id) => byId.get(id))
      .filter((p): p is Pharmacy => !!p);

  const ym = `${year}-${dos(month)}`;
  const mes = MESES[month - 1];
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1; // la grilla arranca el lunes
  const prevMonthDays = new Date(year, month - 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    const date = `${ym}-${dos(d)}`;
    return { d, date, list: enCatalogo(date), dow: new Date(year, month - 1, d).getDay() };
  });
  const missing = days.filter((x) => x.list.length === 0).map((x) => x.d);
  const loaded = daysInMonth - missing.length;
  const savedDaysInMonth = shifts.filter((s) => s.date.startsWith(`${ym}-`)).length;
  const today = hoy();
  const activo = turnoActivo();
  const nowList = enCatalogo(activo.fecha);

  const state: "loading" | "error" | "empty" | "ok" = loading
    ? "loading"
    : loadFailed
      ? "error"
      : loaded === 0
        ? "empty"
        : "ok";

  const openUpload = () => onUploadOpenChange(true);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
      toast.show("Listo: la web y el panel están al día");
    } catch (err) {
      const message =
        err instanceof TypeError
          ? "No hay conexión con el servidor. Revisá internet y reintentá."
          : err instanceof Error && err.message
            ? err.message
            : "No se pudo actualizar.";
      toast.show(message, "error");
    } finally {
      setRefreshing(false);
    }
  };

  const confirmClear = async () => {
    setClearing(true);
    setClearError(null);
    try {
      await onClearMonth(year, month);
      setClearOpen(false);
      toast.show(`${capitalizar(mes)} quedó sin turnos`);
    } catch (err) {
      setClearError(err instanceof Error && err.message ? err.message : "No se pudo borrar el mes.");
    } finally {
      setClearing(false);
    }
  };

  const tono = (isMissing: boolean, isToday: boolean, normal: string) =>
    isMissing
      ? "border-adm-ambar-borde bg-adm-ambar-suave"
      : isToday
        ? "border-adm-acento-borde bg-adm-acento-suave"
        : normal;

  const grid = (
    <Card className="p-1.5 max-md:hidden">
      <div className="grid grid-cols-7 gap-1">
        {DIAS_GRILLA.map((d) => (
          <div key={d} className={cx("px-2.5 pb-1.5 pt-2", LBL)}>
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: offset }, (_, i) => (
          <div key={`out-${i}`} aria-hidden="true" className="min-h-[112px] min-w-0 rounded-xl border border-dashed border-adm-border p-2.5 opacity-60">
            <div className="text-[13px] font-medium text-adm-t70">{prevMonthDays - offset + 1 + i}</div>
          </div>
        ))}
        {days.map(({ d, date, list }) => {
          const isToday = date === today;
          const isMissing = list.length === 0;
          return (
            <button
              key={date}
              type="button"
              onClick={() => onSelectDate(date)}
              aria-label={`${d} de ${mes}`}
              className={cx(
                "flex min-h-[112px] min-w-0 cursor-pointer flex-col gap-1 rounded-xl border p-2.5 text-left transition-colors",
                tono(isMissing, isToday, "border-adm-border hover:border-adm-border-2 hover:bg-adm-card"),
              )}
            >
              <div
                className={cx(
                  "mb-0.5 flex items-center justify-between text-[13px] font-medium",
                  isToday ? "text-adm-acento-texto" : "text-adm-t70",
                )}
              >
                {d}
                {isToday && <Badge tone="ok">Hoy</Badge>}
              </div>
              {list.slice(0, 3).map((p) => (
                <div
                  key={p.id}
                  title={p.nombre}
                  className="truncate text-[12.5px] leading-[1.45] text-adm-t70 notranslate"
                  translate="no"
                >
                  {nombreCorto(p.nombre)}
                </div>
              ))}
              {list.length > 3 && <div className="text-xs text-adm-t55">y {list.length - 3} más</div>}
              {isMissing && (
                <div className="mt-auto flex items-center gap-1 text-xs text-adm-ambar">
                  <TriangleAlert size={13} strokeWidth={1.75} />
                  Sin farmacia
                </div>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );

  const agenda = (
    <div className="flex flex-col gap-1.5 md:hidden">
      {days.map(({ d, date, list, dow }) => {
        const isToday = date === today;
        const isMissing = list.length === 0;
        return (
          <button
            key={date}
            type="button"
            onClick={() => onSelectDate(date)}
            aria-label={`${d} de ${mes}`}
            className={cx(
              "flex min-h-16 w-full cursor-pointer items-center gap-3.5 rounded-[14px] border px-3.5 py-3 text-left",
              tono(isMissing, isToday, "border-adm-border bg-adm-surface"),
            )}
          >
            <div className="flex w-10 shrink-0 flex-col items-center">
              <span className="text-[11px] font-medium text-adm-t55">{DIAS3[dow]}</span>
              <b className="text-xl font-medium leading-[1.1] tracking-[-0.02em] text-adm-text">{d}</b>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {isMissing ? (
                <span className="text-sm text-adm-ambar">Sin farmacia</span>
              ) : (
                <>
                  {list.slice(0, 3).map((p) => (
                    <span key={p.id} className="truncate text-sm text-adm-text notranslate" translate="no">
                      {p.nombre}
                    </span>
                  ))}
                  {list.length > 3 && <span className={LBL}>y {list.length - 3} más</span>}
                </>
              )}
            </div>
            <ChevronRight {...ICON} className="text-adm-t40" />
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col gap-7 max-md:gap-5">
      {/* cabecera */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-adm-acento-texto">Cronograma</span>
          <h1 className="text-[26px] font-semibold leading-[1.2] tracking-[-0.02em] text-adm-text max-md:text-[22px]">
            Turnos
          </h1>
        </div>
        <div className="flex max-md:w-full">
          <Button variant="primary" icon={<FileUp {...ICON} />} className="max-md:flex-1" onClick={openUpload}>
            Subir Excel del mes
          </Button>
        </div>
      </div>

      {state === "error" && (
        <Alert
          tone="red"
          icon={WifiOff}
          action={
            <Button size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          }
        >
          No pudimos traer los turnos. <Muted>Lo que ves puede estar desactualizado.</Muted>
        </Alert>
      )}

      {/* resumen */}
      {state === "loading" ? (
        <div className="grid grid-cols-2 gap-3 min-[1001px]:grid-cols-3">
          <Skeleton className="col-span-2 h-[116px] min-[1001px]:col-span-1" />
          <Skeleton className="h-[116px]" />
          <Skeleton className="h-[116px]" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 min-[1001px]:grid-cols-[1.6fr_1fr_1fr]">
          <div
            className={cx(
              "col-span-2 rounded-[18px] border border-adm-acento-borde bg-adm-acento-suave min-[1001px]:col-span-1",
              STAT,
            )}
          >
            {nowList.length > 0 ? (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-adm-acento-texto">
                    De turno ahora, hasta {activo.terminaHoy ? "hoy" : "mañana"} 8:30
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-adm-acento-texto">
                    <i className="size-1.5 animate-pulse rounded-full bg-adm-acento" />
                    En vivo
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  {nowList.map((p) => (
                    <div
                      key={p.id}
                      className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 text-sm font-medium text-adm-text notranslate"
                      translate="no"
                    >
                      {p.nombre}
                      <span className="truncate text-[13px] font-normal text-adm-t55">{p.direccion}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <span className="text-xs font-medium text-adm-acento-texto">De turno ahora</span>
                <span className="text-[13px] leading-[1.55] text-adm-t55">
                  Todavía no hay turnos cargados para hoy.
                </span>
              </>
            )}
          </div>
          <Card className={STAT}>
            <span className={LBL}>Días cargados</span>
            <span className={STAT_V}>
              {loaded} <small className="text-[15px] font-normal text-adm-t40">/ {daysInMonth}</small>
            </span>
            <div className="h-1 overflow-hidden rounded-full bg-adm-t10">
              <i
                className="block h-full rounded-full bg-adm-acento"
                style={{ width: `${(loaded / daysInMonth) * 100}%` }}
              />
            </div>
          </Card>
          <Card className={STAT}>
            <span className={LBL}>Días sin farmacia</span>
            <span className={STAT_V}>{missing.length}</span>
            {state !== "empty" && (
              <span className="text-[13px] leading-[1.55] text-adm-t55">{textoFaltantes(missing)}</span>
            )}
          </Card>
        </div>
      )}

      {/* barra del mes */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 max-md:w-full">
          <IconButton label="Mes anterior" bordered onClick={onPrevMonth}>
            <ChevronLeft {...ICON} />
          </IconButton>
          <h2 className="min-w-[170px] text-center text-lg font-semibold tracking-[-0.01em] text-adm-text max-md:min-w-0 max-md:flex-1">
            {capitalizar(mes)} {year}
          </h2>
          <IconButton label="Mes siguiente" bordered onClick={onNextMonth}>
            <ChevronRight {...ICON} />
          </IconButton>
        </div>
        <div className="flex flex-wrap items-center gap-2 max-md:w-full max-md:justify-between">
          <span className={LBL}>El turno cambia a las 8:30</span>
          <Button
            size="sm"
            icon={<RefreshCw {...ICON} />}
            loading={refreshing}
            aria-label="Actualizar"
            className="max-md:w-10 max-md:px-0"
            onClick={refresh}
          >
            <span className="max-md:hidden">Actualizar</span>
          </Button>
          {(state === "ok" || state === "error") && (
            <Button
              variant="ghost"
              tone="danger"
              size="sm"
              icon={<Eraser {...ICON} />}
              onClick={() => {
                setClearError(null);
                setClearOpen(true);
              }}
            >
              Limpiar mes
            </Button>
          )}
        </div>
      </div>

      {/* calendario: los cuatro estados */}
      {state === "loading" && (
        <>
          <Card className="p-1.5 max-md:hidden">
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }, (_, i) => (
                <div key={i}>
                  <Skeleton className="h-[112px] rounded-xl" />
                </div>
              ))}
            </div>
          </Card>
          <div className="flex flex-col gap-1.5 md:hidden">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i}>
                <Skeleton className="h-16" />
              </div>
            ))}
          </div>
        </>
      )}

      {state === "empty" && (
        <Empty
          icon={CalendarPlus}
          title={`${capitalizar(mes)} todavía no tiene turnos`}
          action={
            <Button variant="primary" icon={<FileUp {...ICON} />} onClick={openUpload}>
              Subir Excel del mes
            </Button>
          }
        >
          Subí el Excel que manda el Colegio y el calendario se llena solo. También podés tocar cada día y cargarlo a
          mano.
        </Empty>
      )}

      {state !== "loading" && (
        <>
          {grid}
          {agenda}
        </>
      )}

      {/* cajón del día */}
      {selectedDate && (
        <DayDrawer
          key={selectedDate}
          date={selectedDate}
          pharmacies={pharmacies}
          assignedIds={shiftByDate.get(selectedDate)?.pharmacyIds || []}
          onClose={() => onSelectDate(null)}
          onAdd={(id) => onAddPharmacy(selectedDate, id)}
          onRemove={(id) => onRemovePharmacy(selectedDate, id)}
        />
      )}

      {/* confirmar limpiar (#mClear): sin X, como la maqueta */}
      <Modal
        open={clearOpen}
        onClose={() => {
          if (!clearing) setClearOpen(false);
        }}
        title={`¿Borrar los turnos de ${mes}?`}
        size="sm"
        showClose={false}
        footer={
          <>
            <Button onClick={() => setClearOpen(false)} disabled={clearing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmClear} loading={clearing}>
              {clearing ? "Borrando…" : "Borrar el mes"}
            </Button>
          </>
        }
      >
        <p className="text-[13px] leading-[1.55] text-adm-t55">
          {savedDaysInMonth === 1 ? "Se borra 1 día." : `Se borran los ${savedDaysInMonth} días.`} La web y el bot
          quedan sin farmacias de turno hasta que vuelvas a cargar el mes.
        </p>
        {clearError && <Alert tone="red">{clearError}</Alert>}
      </Modal>

      {/* subir el Excel: el estado que lo abre vive en App.tsx; se monta acá para quedar dentro de .admin */}
      {uploadOpen && (
        <OriginalExcelUploader
          month={month}
          year={year}
          onClose={() => onUploadOpenChange(false)}
          onUploadSuccess={(msg) => {
            toast.show(msg);
            onUploaded();
          }}
        />
      )}
    </div>
  );
}
